/* ============================================================================
   Dashboard — the Requests landing page.
   KPI tiles (scoped to what the signed-in user can see) plus a filterable,
   compact request list with Direction-C status pills and priority.
   ============================================================================ */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, isApiError } from '../../lib/api';
import { useAuth, hasRole } from '../../lib/auth';
import {
  useLookups,
  statusPillClass,
  priorityPillClass,
  type RequestRow,
} from './data';

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

interface Kpi {
  label: string;
  value: number;
  accent?: boolean;
}

function KpiTile({ label, value, accent }: Kpi) {
  return (
    <div
      className="card"
      style={{
        padding: '16px 18px',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        borderTop: accent ? '3px solid var(--uva-orange)' : '3px solid var(--uva-navy)',
      }}
    >
      <span
        className="mono"
        style={{ fontSize: 'var(--fs-xs)', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ink-3)' }}
      >
        {label}
      </span>
      <span style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--fs-2xl)', fontWeight: 600, color: 'var(--uva-navy)' }}>
        {value}
      </span>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { lookups } = useLookups();

  const [rows, setRows] = useState<RequestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [q, setQ] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (priority) params.set('priority', priority);
    if (q.trim()) params.set('q', q.trim());
    const qs = params.toString();
    apiGet<RequestRow[]>(`/api/requests${qs ? `?${qs}` : ''}`)
      .then((data) => {
        if (active) {
          setRows(Array.isArray(data) ? data : []);
          setError(null);
        }
      })
      .catch((e) => {
        if (active && isApiError(e) && e.status !== 404) setError(e.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [status, priority, q]);

  const kpis = useMemo<Kpi[]>(() => {
    const today = todayISO();
    // Canceled requests keep their history but drop out of every active count.
    const submitted = rows.filter((r) => !r.isDraft && r.statusCode !== 'canceled');
    const open = submitted.filter((r) => r.statusCode !== 'complete');
    const inProgress = submitted.filter((r) => r.statusCode === 'in_progress').length;
    const waiting = submitted.filter(
      (r) => r.statusCode === 'waiting_content' || r.statusCode === 'waiting_approval',
    ).length;
    const complete = submitted.filter((r) => r.statusCode === 'complete').length;
    const overdue = open.filter((r) => r.neededBy && r.neededBy < today).length;
    return [
      { label: 'Open', value: open.length, accent: true },
      { label: 'In progress', value: inProgress },
      { label: 'Waiting', value: waiting },
      { label: 'Overdue', value: overdue },
      { label: 'Complete', value: complete },
    ];
  }, [rows]);

  const canCreate = hasRole(user, ['Requester', 'Admin', 'SystemAdmin']);
  const hasFilters = Boolean(status || priority || q.trim());

  return (
    <div className="container page">
      <div className="page-title-row">
        <div>
          <p className="eyebrow">Creative Requests</p>
          <h1>Dashboard</h1>
        </div>
        {canCreate && (
          <button className="btn btn-primary" onClick={() => navigate('/requests/new')}>
            + New request
          </button>
        )}
      </div>

      {/* KPI tiles */}
      <div
        className="grid"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', marginBottom: 24 }}
      >
        {kpis.map((k) => (
          <KpiTile key={k.label} {...k} />
        ))}
      </div>

      {/* Filters */}
      <div className="row" style={{ flexWrap: 'wrap', marginBottom: 16, gap: 10 }}>
        <input
          className="in"
          style={{ maxWidth: 280 }}
          placeholder="Search title or ticket…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select className="in" style={{ maxWidth: 200 }} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {lookups.statuses.map((s) => (
            <option key={s.code} value={s.code}>
              {s.name}
            </option>
          ))}
        </select>
        <select className="in" style={{ maxWidth: 180 }} value={priority} onChange={(e) => setPriority(e.target.value)}>
          <option value="">All priorities</option>
          {lookups.priorities.map((p) => (
            <option key={p.code} value={p.code}>
              {p.name}
            </option>
          ))}
        </select>
        {hasFilters && (
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => {
              setStatus('');
              setPriority('');
              setQ('');
            }}
          >
            Clear
          </button>
        )}
        <span className="grow" />
        <span className="meta">{rows.length} request{rows.length === 1 ? '' : 's'}</span>
      </div>

      {loading ? (
        <div className="card" style={{ display: 'grid', placeItems: 'center', padding: 48 }}>
          <div className="spinner" />
        </div>
      ) : error ? (
        <div className="card empty">
          <h3>Couldn't load requests</h3>
          <p className="muted">{error}</p>
        </div>
      ) : rows.length === 0 ? (
        <div className="card empty">
          <h3>{hasFilters ? 'No matching requests' : 'No requests yet'}</h3>
          <p className="muted">
            {hasFilters
              ? 'Try clearing the filters.'
              : canCreate
                ? 'Create the first creative request to get started.'
                : 'Nothing has been assigned to you yet.'}
          </p>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="tbl tbl-compact">
            <thead>
              <tr>
                <th>Ticket</th>
                <th>Title</th>
                <th>Requester</th>
                <th>Designer</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Requested</th>
                <th>Needed by</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const pc = priorityPillClass(r.priorityCode);
                return (
                  <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/requests/${r.id}`)}>
                    <td className="mono">
                      {r.ticketId}
                      {r.isDraft ? <span className="pill pill-warning" style={{ marginLeft: 6 }}>Draft</span> : null}
                    </td>
                    <td style={{ fontWeight: 600 }}>{r.title}</td>
                    <td>{r.requesterName}</td>
                    <td>{r.designerName ?? <span className="muted">—</span>}</td>
                    <td>
                      {r.priority ? (
                        pc ? (
                          <span className={`pill ${pc}`}>{r.priority}</span>
                        ) : (
                          <span className="meta">{r.priority}</span>
                        )
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td>
                      <span className={`pill ${statusPillClass(r.statusCode)}`}>{r.designStatus}</span>
                    </td>
                    <td className="mono">{r.requestDate ?? '—'}</td>
                    <td className="mono">{r.neededBy ?? '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
